// src/utils/sendMessages.ts
import axios from 'axios';
import { Message } from '../config/types';
import MSALSingleton from './msalInstance';
import { environmentVars } from '../config/authConfig';
import { getAnonymousId } from '../utils/anonymousUser';
const { apiUrl, apiKey } = environmentVars;

interface SendMessageParams {
  prompt: string;
  history: Message[];
}

interface SendMessageResponse {
  message: Message;
  assistantMessage: Message;
}

async function getAuthHeaders() {
  const instance = MSALSingleton.getInstance();
  const accounts = instance.getAllAccounts();

  if (accounts.length === 0) {
    return null;
  }

  try {
    const silentResult = await instance.acquireTokenSilent({
      scopes: ['User.Read'],
      account: accounts[0]
    });

    // Get the authentication provider from the account's idTokenClaims
    const provider = accounts[0].idTokenClaims?.iss?.includes('google') ? 'google' : 'microsoft';

    return {
      'X-Auth-Token': silentResult.accessToken,
      'X-Auth-Provider': provider
    };
  } catch (error) {
    console.error('Failed to acquire token:', error);
    return null;
  }
}

export const sendMessage = async ({
  prompt,
  history
}: SendMessageParams): Promise<SendMessageResponse> => {
  if (!prompt.trim()) {
    throw new Error('Prompt cannot be empty');
  }

  const userMessage: Message = {
    role: 'user',
    content: prompt.trim()
  };

  if (!apiUrl) {
    throw new Error('API URL is not defined');
  }

  // Initialize MSAL if not already initialized
  if (!MSALSingleton.isInitialized()) {
    await MSALSingleton.initialize();
  }

  // Get current user identifier (email for authenticated users, anonymous ID for others)
  const instance = MSALSingleton.getInstance();
  const accounts = instance.getAllAccounts();
  const currentUserEmail = accounts.length === 0 ? getAnonymousId() : accounts[0].username;

  // Get auth headers if user is authenticated
  const authHeaders = await getAuthHeaders();

  const payload = {
    prompt: userMessage.content,
    mode: 'simple',
    history,
    user: currentUserEmail,
  };

  if (!apiKey) {
    throw new Error('API key is not defined');
  }

  const headers: Record<string, string> = {
    'x-functions-key': apiKey,
    ...(authHeaders || {}) // Only include auth headers if they exist
  };

  const result = await axios.post(
    apiUrl,
    payload,
    { headers }
  );

  let responseContent: string | undefined;

  if (Array.isArray(result.data.response)) {
    responseContent = result.data.response[result.data.response.length - 1]?.content;
  } else if (typeof result.data.response === 'object' && result.data.response !== null) {
    responseContent = result.data.response.content;
  } else if (typeof result.data.response === 'string') {
    responseContent = result.data.response;
  }

  if (!responseContent) {
    console.error('Invalid response format:', result.data);
    throw new Error('Invalid response format from API');
  }

  const assistantMessage: Message = {
    role: 'assistant',
    content: responseContent
  };

  return {
    message: userMessage,
    assistantMessage
  };
};